// src/features/reports/ReportsPage.tsx
// EXECUTIVE REPORTS & ANALYTICS DASHBOARD (ERP & POS)

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { getBranches } from '@/features/settings/services/settingsApi';
import {
    getSalesSummary, getStockValuation, getTopCustomers,
    getProfitLossReport, getExportUrl, downloadReportFile
} from './services/reportsApi';
import {
    BarChart3, TrendingUp, Package, Users, DollarSign,
    RotateCcw, Store, ShoppingCart, Star, Clock,
    FileText, FileSpreadsheet, Sparkles, Zap, Target, Award, AlertTriangle,
    TrendingDown, Wallet, Receipt, CreditCard,
    Calendar, Activity, Layers, Search, Filter, ChevronRight,
    Building2, Scale, Percent, RefreshCw, CheckCircle2, ArrowUpRight,
    PieChart as PieIcon, Download, Lightbulb, ShieldCheck
} from 'lucide-react';
import {
    ResponsiveContainer, AreaChart, Area, BarChart, Bar,
    XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend
} from 'recharts';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

interface ReportCard {
    title: string;
    description: string;
    icon: React.ElementType;
    href: string;
    color: string;
    type: string;
    badge?: string;
    featured?: boolean;
}

const REPORT_SECTIONS: { category: string; icon: React.ElementType; gradient: string; reports: ReportCard[] }[] = [
    {
        category: 'Financial Statements & Accounting',
        icon: DollarSign,
        gradient: 'from-emerald-500 to-teal-700',
        reports: [
            { title: 'Profit & Loss (P&L)', description: 'Income statement, COGS, operating overhead & net profit', icon: Scale, href: '/reports/finance/p-and-l', color: 'from-emerald-500 to-teal-600', type: 'p_and_l', featured: true, badge: 'Financials' },
            { title: 'Tax & VAT Collected', description: 'VAT and sales tax collected by period for remittance', icon: Receipt, href: '/reports/finance/tax', color: 'from-blue-500 to-indigo-600', type: 'tax', featured: true },
            { title: 'Daily Revenue Inflow', description: 'Day-by-day revenue with running totals and net sales', icon: Calendar, href: '/reports/finance/daily-revenue', color: 'from-teal-500 to-emerald-600', type: 'daily_revenue' },
            { title: 'Discount Summary', description: 'Discounts given by type, reason, and promotional cost', icon: TrendingDown, href: '/reports/finance/discounts', color: 'from-rose-500 to-pink-600', type: 'discount' },
        ],
    },
    {
        category: 'Sales Analytics',
        icon: TrendingUp,
        gradient: 'from-blue-500 to-indigo-600',
        reports: [
            { title: 'Sales Summary', description: 'Daily revenue, orders, tax, discounts & gross profit', icon: BarChart3, href: '/reports/sales/summary', color: 'from-emerald-500 to-teal-600', type: 'sales_summary', featured: true },
            { title: 'Sales by Product', description: 'Top selling products by revenue, cost, and quantity', icon: Package, href: '/reports/sales/by-product', color: 'from-blue-500 to-indigo-600', type: 'sales_by_product' },
            { title: 'Sales by Category', description: 'Revenue and margin contribution by product category', icon: Layers, href: '/reports/sales/by-category', color: 'from-cyan-500 to-sky-600', type: 'sales_by_category' },
            { title: 'Sales by Cashier', description: 'Staff sales performance, average order, and void counts', icon: Users, href: '/reports/sales/by-cashier', color: 'from-purple-500 to-violet-600', type: 'sales_by_cashier' },
            { title: 'Sales by Payment', description: 'Cash, POS card, bank transfer distribution & share', icon: CreditCard, href: '/reports/sales/by-payment', color: 'from-amber-500 to-orange-600', type: 'sales_by_payment' },
            { title: 'Voids & Refunds', description: 'Audit trail of voided items and customer refund reasons', icon: RotateCcw, href: '/reports/sales/void-refund', color: 'from-rose-500 to-pink-600', type: 'void_refund', badge: 'Audit' },
        ],
    },
    {
        category: 'Inventory & Stock Valuation',
        icon: Store,
        gradient: 'from-cyan-500 to-blue-600',
        reports: [
            { title: 'Stock Valuation', description: 'Current stock value at cost price and retail price', icon: Store, href: '/reports/inventory/valuation', color: 'from-teal-500 to-emerald-600', type: 'stock_valuation', featured: true },
            { title: 'Stock Movement', description: 'Comprehensive inventory transactions and audit history', icon: Activity, href: '/reports/inventory/movement', color: 'from-cyan-500 to-sky-600', type: 'stock_movement' },
            { title: 'Low Stock Alert', description: 'Products currently below minimum reorder threshold', icon: AlertTriangle, href: '/reports/inventory/low-stock', color: 'from-amber-500 to-orange-600', type: 'low_stock', badge: 'Alert' },
            { title: 'Stock Aging', description: 'Inventory turnover speed and slow-aging capital tie-up', icon: Clock, href: '/reports/inventory/aging', color: 'from-slate-500 to-slate-700', type: 'stock_aging' },
        ],
    },
    {
        category: 'Customer Intelligence',
        icon: Users,
        gradient: 'from-purple-500 to-violet-600',
        reports: [
            { title: 'Top Customers', description: 'Highest spending customers ranking & lifetime value', icon: Star, href: '/reports/customers/top', color: 'from-amber-400 to-yellow-500', type: 'top_customers', featured: true },
            { title: 'Purchase History', description: 'Individual customer transaction and order history log', icon: ShoppingCart, href: '/reports/customers/purchase-history', color: 'from-blue-500 to-indigo-600', type: 'customer_purchase_history' },
            { title: 'Loyalty Summary', description: 'Points earned, redeemed, and tier distribution breakdown', icon: Award, href: '/reports/customers/loyalty', color: 'from-purple-500 to-violet-600', type: 'loyalty_summary' },
            { title: 'Customer Debt', description: 'Outstanding customer balances, receivables, and aging', icon: Wallet, href: '/reports/customers/debt', color: 'from-rose-500 to-pink-600', type: 'customer_debt', badge: 'Risk' },
        ],
    },
    {
        category: 'Staff & Shift Reconciliation',
        icon: Users,
        gradient: 'from-indigo-500 to-blue-600',
        reports: [
            { title: 'Staff Performance', description: 'Sales volume, speed, void rates, and efficiency per staff', icon: Target, href: '/reports/staff/performance', color: 'from-emerald-500 to-teal-600', type: 'staff_performance' },
            { title: 'Shift Summary', description: 'Cash drawer opening/closing and cash variance report', icon: Clock, href: '/reports/staff/shifts', color: 'from-blue-500 to-indigo-600', type: 'shift_summary' },
        ],
    },
    {
        category: 'Product Performance',
        icon: Package,
        gradient: 'from-orange-500 to-amber-600',
        reports: [
            { title: 'Top Selling Items', description: 'Best performing products by units sold and velocity', icon: Zap, href: '/reports/products/top-selling', color: 'from-emerald-500 to-teal-600', type: 'top_selling' },
            { title: 'Slow Moving Stock', description: 'Dead stock and low turnover products tying up cash', icon: TrendingDown, href: '/reports/products/slow-moving', color: 'from-amber-500 to-orange-600', type: 'slow_moving' },
            { title: 'Profit Margin Analysis', description: 'Product markup, unit profitability, and margin rankings', icon: DollarSign, href: '/reports/products/profit-margin', color: 'from-green-500 to-emerald-600', type: 'profit_margin' },
        ],
    },
];

type ActiveTab = 'dashboard' | 'p_and_l' | 'catalog' | 'export_center';

const PIE_COLORS = ['#0f766e', '#0284c7', '#8b5cf6', '#f59e0b', '#ec4899', '#64748b'];

export function ReportsPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [activeTab, setActiveTab] = useState<ActiveTab>('dashboard');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeCategory, setActiveCategory] = useState<string>('all');
    const [selectedBranchId, setSelectedBranchId] = useState<string>('');
    const [datePreset, setDatePreset] = useState<'today' | '7d' | '30d' | 'month' | 'last_month' | 'all'>('month');
    const [customRange, setCustomRange] = useState({ startDate: '', endDate: '' });

    // Branches query
    const { data: branches } = useQuery({
        queryKey: ['branches-reports-hub', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });
    const branchesList = (branches || []) as any[];

    // Calculate effective start/end dates based on preset
    const effectiveDates = useMemo(() => {
        const now = new Date();
        const endStr = now.toISOString().split('T')[0];
        if (datePreset === 'today') {
            return { startDate: endStr, endDate: endStr };
        }
        if (datePreset === '7d') {
            const start = new Date();
            start.setDate(now.getDate() - 7);
            return { startDate: start.toISOString().split('T')[0], endDate: endStr };
        }
        if (datePreset === '30d') {
            const start = new Date();
            start.setDate(now.getDate() - 30);
            return { startDate: start.toISOString().split('T')[0], endDate: endStr };
        }
        if (datePreset === 'month') {
            const start = new Date(now.getFullYear(), now.getMonth(), 1);
            return { startDate: start.toISOString().split('T')[0], endDate: endStr };
        }
        if (datePreset === 'last_month') {
            const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const end = new Date(now.getFullYear(), now.getMonth(), 0);
            return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
        }
        return {
            startDate: customRange.startDate || undefined,
            endDate: customRange.endDate || undefined,
        };
    }, [datePreset, customRange]);

    const reportParams = useMemo(() => ({
        startDate: effectiveDates.startDate,
        endDate: effectiveDates.endDate,
        branchId: selectedBranchId || undefined,
    }), [effectiveDates, selectedBranchId]);

    // Fast parallel queries for dashboard analytics
    const { data: salesSummary, isLoading: salesLoading, refetch: refetchSales } = useQuery({
        queryKey: ['reports-hub-sales', tenantId, reportParams],
        queryFn: () => getSalesSummary(tenantId, reportParams),
        enabled: !!tenantId,
    });

    const { data: pandlData, isLoading: pandlLoading, refetch: refetchPandl } = useQuery({
        queryKey: ['reports-hub-pandl', tenantId, reportParams],
        queryFn: () => getProfitLossReport(tenantId, reportParams),
        enabled: !!tenantId,
    });

    const { data: stockValuation, isLoading: stockLoading } = useQuery({
        queryKey: ['reports-hub-stock', tenantId, selectedBranchId],
        queryFn: () => getStockValuation(tenantId, selectedBranchId || undefined),
        enabled: !!tenantId,
    });

    const { data: topCustomers } = useQuery({
        queryKey: ['reports-hub-customers', tenantId, reportParams],
        queryFn: () => getTopCustomers(tenantId, { ...reportParams, limit: 5 }),
        enabled: !!tenantId,
    });

    const isDataLoading = salesLoading || pandlLoading;

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const formatNumber = (val: string | number) => parseFloat(String(val || '0')).toLocaleString('en-NG');

    // Financial numbers
    const summary = pandlData?.summary || salesSummary?.summary || {};
    const grossSales = parseFloat(summary.grossSales || summary.totalRevenue || '0');
    const netSales = parseFloat(summary.netSales || summary.totalRevenue || '0');
    const cogs = parseFloat(summary.costOfGoodsSold || summary.totalCost || '0');
    const grossProfit = parseFloat(summary.grossProfit || '0');
    const totalExpenses = parseFloat(summary.totalOperatingExpenses || '0');
    const netOperatingProfit = parseFloat(summary.netOperatingProfit || (grossProfit - totalExpenses) || '0');
    const completedOrders = summary.completedOrders || summary.totalSales || 0;
    const aov = completedOrders > 0 ? (grossSales / completedOrders) : 0;

    // Charts data prep
    const dailyChartData = useMemo(() => {
        const breakdown = salesSummary?.dailyBreakdown || [];
        return breakdown.map((d: any) => ({
            date: d.date?.slice(5) || d.date,
            Revenue: parseFloat(d.revenue || '0'),
            Cost: parseFloat(d.cost || '0'),
            Profit: parseFloat(d.profit || '0'),
        }));
    }, [salesSummary]);

    const paymentMethodsData = useMemo(() => {
        const methods = salesSummary?.paymentBreakdown || [];
        return methods.map((m: any) => ({
            name: m.method?.toUpperCase() || 'OTHER',
            value: parseFloat(m.amount || '0'),
        }));
    }, [salesSummary]);

    const hourlyData = useMemo(() => {
        const hours = salesSummary?.hourlyBreakdown || [];
        return hours.map((h: any) => ({
            hour: `${h.hour}:00`,
            Revenue: parseFloat(h.revenue?.toString() || '0'),
            Orders: h.count || 0,
        }));
    }, [salesSummary]);

    // Handle One-Click Tenant Export
    const handleQuickExport = (type: string, format: 'pdf' | 'excel') => {
        downloadReportFile(tenantId, type, format, reportParams);
    };

    // Filter reports in catalog
    const filteredSections = useMemo(() => {
        return REPORT_SECTIONS.map(section => {
            const matchesCategory = activeCategory === 'all' || section.category.toLowerCase().includes(activeCategory.toLowerCase());
            if (!matchesCategory) return { ...section, reports: [] };

            const filteredReports = section.reports.filter(r =>
                r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                r.description.toLowerCase().includes(searchQuery.toLowerCase())
            );
            return { ...section, reports: filteredReports };
        }).filter(s => s.reports.length > 0);
    }, [searchQuery, activeCategory]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 pb-20">
            {/* Header with Glassmorphism */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        {/* Title & Organization Subtitle */}
                        <div className="flex items-center gap-4">
                            <motion.div whileHover={{ rotate: -5, scale: 1.05 }} className="p-2.5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg shadow-emerald-500/25 text-white">
                                <BarChart3 className="w-6 h-6" />
                            </motion.div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h1 className="text-2xl font-bold text-slate-900">Executive Reports & BI</h1>
                                    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-200 uppercase tracking-wider">
                                        ERP Intelligence
                                    </span>
                                </div>
                                <p className="text-sm text-slate-500">
                                    Financial statements, POS sales analytics, inventory valuation, and tenant-branded exports
                                </p>
                            </div>
                        </div>

                        {/* Top Global Controls: Branch & Date Presets */}
                        <div className="flex flex-wrap items-center gap-2">
                            {/* Branch Selector */}
                            <select
                                value={selectedBranchId}
                                onChange={(e) => setSelectedBranchId(e.target.value)}
                                className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                            >
                                <option value="">🏢 All Branches</option>
                                {branchesList.map(b => (
                                    <option key={b.id} value={b.id}>{b.name}</option>
                                ))}
                            </select>

                            {/* Date Presets */}
                            <div className="flex items-center bg-slate-100 p-1 rounded-xl text-xs font-bold">
                                {[
                                    { key: 'today', label: 'Today' },
                                    { key: '7d', label: '7D' },
                                    { key: 'month', label: 'This Month' },
                                    { key: 'last_month', label: 'Last Month' },
                                    { key: 'all', label: 'All Time' },
                                ].map(p => (
                                    <button
                                        key={p.key}
                                        onClick={() => setDatePreset(p.key as any)}
                                        className={clsx(
                                            'px-3 py-1.5 rounded-lg transition-all',
                                            datePreset === p.key
                                                ? 'bg-white text-slate-900 shadow-xs'
                                                : 'text-slate-500 hover:text-slate-800'
                                        )}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>

                            {/* Refresh */}
                            <button
                                onClick={() => { refetchSales(); refetchPandl(); }}
                                className="p-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl transition-all"
                                title="Refresh Analytics"
                            >
                                <RefreshCw className={clsx('w-4 h-4', isDataLoading && 'animate-spin text-teal-600')} />
                            </button>

                            {/* One-Click Executive Export */}
                            <button
                                onClick={() => handleQuickExport('p_and_l', 'pdf')}
                                className="flex items-center gap-1.5 px-3.5 py-2 bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 rounded-xl text-xs font-bold shadow-xs transition-all"
                                title="Download Tenant Profit & Loss PDF"
                            >
                                <FileText className="w-3.5 h-3.5" /> P&L PDF
                            </button>
                            <button
                                onClick={() => handleQuickExport('p_and_l', 'excel')}
                                className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-xs transition-all"
                                title="Download Tenant Profit & Loss Excel"
                            >
                                <FileSpreadsheet className="w-3.5 h-3.5" /> P&L Excel
                            </button>
                        </div>
                    </div>

                    {/* Navigation Tabs */}
                    <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit mt-4 overflow-x-auto">
                        <TabButton
                            active={activeTab === 'dashboard'}
                            onClick={() => setActiveTab('dashboard')}
                            icon={BarChart3}
                            label="Executive BI Dashboard"
                        />
                        <TabButton
                            active={activeTab === 'p_and_l'}
                            onClick={() => setActiveTab('p_and_l')}
                            icon={Scale}
                            label="Financial Statements (P&L)"
                            badge="Income Statement"
                        />
                        <TabButton
                            active={activeTab === 'catalog'}
                            onClick={() => setActiveTab('catalog')}
                            icon={Layers}
                            label="All Reports Catalog (23)"
                        />
                        <TabButton
                            active={activeTab === 'export_center'}
                            onClick={() => setActiveTab('export_center')}
                            icon={Download}
                            label="Universal Export Center"
                            badge="Tenant Branded"
                        />
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                {/* TAB 1: EXECUTIVE BI DASHBOARD */}
                {activeTab === 'dashboard' && (
                    <div className="space-y-6">
                        {/* 6 Core Executive KPI Cards */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3.5">
                            <StatWidget
                                label="Gross Revenue"
                                value={formatNaira(grossSales)}
                                subtitle={`${formatNumber(completedOrders)} completed orders`}
                                icon={DollarSign}
                                gradient="from-blue-600 to-indigo-700"
                            />
                            <StatWidget
                                label="Net Sales Revenue"
                                value={formatNaira(netSales)}
                                subtitle="After discounts & returns"
                                icon={TrendingUp}
                                gradient="from-teal-500 to-emerald-600"
                            />
                            <StatWidget
                                label="Cost of Goods (COGS)"
                                value={formatNaira(cogs)}
                                subtitle={`${netSales > 0 ? ((cogs / netSales) * 100).toFixed(1) : 0}% of net sales`}
                                icon={TrendingDown}
                                gradient="from-rose-500 to-red-600"
                            />
                            <StatWidget
                                label="Gross Operating Profit"
                                value={formatNaira(grossProfit)}
                                subtitle={`${summary.grossMarginPct || 0}% gross margin`}
                                icon={Percent}
                                gradient="from-amber-500 to-orange-600"
                            />
                            <StatWidget
                                label="Operating Overhead"
                                value={formatNaira(totalExpenses)}
                                subtitle={`${pandlData?.expenseBreakdown?.length || 0} expense categories`}
                                icon={Building2}
                                gradient="from-purple-500 to-violet-600"
                            />
                            <StatWidget
                                label="Net Profit (EBITDA)"
                                value={formatNaira(netOperatingProfit)}
                                subtitle={`${summary.netProfitMarginPct || 0}% net income`}
                                icon={Scale}
                                gradient={netOperatingProfit >= 0 ? "from-emerald-500 to-teal-700" : "from-rose-600 to-red-800"}
                            />
                        </div>

                        {/* Executive Insights & Operational Alerts */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <InsightCard
                                title="Operating Profitability"
                                description={grossProfit > 0
                                    ? `Generating ${summary.grossMarginPct || 0}% gross margin. Operating expenses consume ${grossProfit > 0 ? ((totalExpenses / grossProfit) * 100).toFixed(1) : 0}% of gross profits.`
                                    : 'Awaiting sales data to calculate profitability ratio.'}
                                icon={Lightbulb}
                                color="emerald"
                            />
                            <InsightCard
                                title="Average Ticket Value"
                                description={`Average basket size is ${formatNaira(aov)} per transaction across ${formatNumber(completedOrders)} sales.`}
                                icon={ShoppingCart}
                                color="blue"
                            />
                            <InsightCard
                                title="Inventory Health"
                                description={`Total stock value is ${formatNaira(stockValuation?.summary?.totalRetailValue || 0)} across ${formatNumber(stockValuation?.summary?.totalItems || 0)} catalogue items.`}
                                icon={Store}
                                color="amber"
                            />
                            <InsightCard
                                title="Tax Compliance"
                                description={`${formatNaira(summary.taxCollected || salesSummary?.summary?.totalTax || 0)} collected in sales VAT/tax ready for audit remittance.`}
                                icon={ShieldCheck}
                                color="purple"
                            />
                        </div>

                        {/* Visual Analytics Charts Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                            {/* Revenue vs Cost vs Profit Trend (8 Cols) */}
                            <div className="lg:col-span-8 bg-white rounded-3xl border border-slate-200 p-6 shadow-xs">
                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-6">
                                    <div>
                                        <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                                            <Activity className="w-4 h-4 text-teal-600" />
                                            Daily Financial Velocity (Revenue vs COGS vs Gross Profit)
                                        </h3>
                                        <p className="text-xs text-slate-400 mt-0.5">Day-by-day revenue curve and underlying cost of production</p>
                                    </div>
                                    <div className="flex items-center gap-4 text-xs font-semibold">
                                        <span className="flex items-center gap-1.5 text-teal-600"><span className="w-2.5 h-2.5 rounded-full bg-teal-500" /> Revenue</span>
                                        <span className="flex items-center gap-1.5 text-rose-500"><span className="w-2.5 h-2.5 rounded-full bg-rose-500" /> Cost</span>
                                        <span className="flex items-center gap-1.5 text-amber-500"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Profit</span>
                                    </div>
                                </div>

                                {dailyChartData.length === 0 ? (
                                    <div className="h-64 flex items-center justify-center text-xs text-slate-400">
                                        No daily transaction data recorded for this period
                                    </div>
                                ) : (
                                    <div className="h-72 w-full">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={dailyChartData}>
                                                <defs>
                                                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#0f766e" stopOpacity={0.25} />
                                                        <stop offset="95%" stopColor="#0f766e" stopOpacity={0.0} />
                                                    </linearGradient>
                                                    <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.25} />
                                                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#64748b' }} />
                                                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={(v) => `₦${(v / 1000).toFixed(0)}k`} />
                                                <Tooltip
                                                    formatter={(val: any) => [`₦${parseFloat(val).toLocaleString()}`, '']}
                                                    contentStyle={{ backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', fontSize: '11px', fontWeight: 'bold' }}
                                                />
                                                <Area type="monotone" dataKey="Revenue" stroke="#0f766e" strokeWidth={2.5} fillOpacity={1} fill="url(#colorRev)" />
                                                <Area type="monotone" dataKey="Profit" stroke="#f59e0b" strokeWidth={2} fillOpacity={1} fill="url(#colorProfit)" />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                            </div>

                            {/* Payment Methods Distribution (4 Cols) */}
                            <div className="lg:col-span-4 bg-white rounded-3xl border border-slate-200 p-6 shadow-xs flex flex-col justify-between">
                                <div>
                                    <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2 mb-1">
                                        <PieIcon className="w-4 h-4 text-indigo-600" />
                                        Payment Method Distribution
                                    </h3>
                                    <p className="text-xs text-slate-400 mb-4">Cash vs electronic payments reconciliation</p>

                                    {paymentMethodsData.length === 0 ? (
                                        <div className="h-56 flex items-center justify-center text-xs text-slate-400">
                                            No payment methods recorded
                                        </div>
                                    ) : (
                                        <div className="h-56 w-full">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={paymentMethodsData}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={50}
                                                        outerRadius={80}
                                                        paddingAngle={4}
                                                        dataKey="value"
                                                    >
                                                        {paymentMethodsData.map((_: any, index: number) => (
                                                            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip formatter={(val: any) => [`₦${parseFloat(val).toLocaleString()}`, 'Collected']} />
                                                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                    )}
                                </div>

                                <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                                    <span className="text-slate-500">Need full payment audit?</span>
                                    <button
                                        onClick={() => navigate('/reports/sales/by-payment')}
                                        className="text-teal-700 font-bold hover:underline flex items-center gap-1"
                                    >
                                        Payment Report <ChevronRight size={12} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Hourly Sales Traffic Heatmap / Bar */}
                        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-xs">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
                                <div>
                                    <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                                        <Clock className="w-4 h-4 text-amber-500" />
                                        Peak Trading Hours & Cashier Rush Distribution
                                    </h3>
                                    <p className="text-xs text-slate-400 mt-0.5">Identify peak traffic windows for optimal shift and cashier scheduling</p>
                                </div>
                            </div>

                            {hourlyData.length === 0 ? (
                                <div className="h-44 flex items-center justify-center text-xs text-slate-400">
                                    No hourly trade logs available for this period
                                </div>
                            ) : (
                                <div className="h-52 w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={hourlyData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                            <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#64748b' }} />
                                            <YAxis tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={(v) => `₦${(v / 1000).toFixed(0)}k`} />
                                            <Tooltip formatter={(v: any) => [`₦${parseFloat(v).toLocaleString()}`, 'Revenue']} />
                                            <Bar dataKey="Revenue" fill="#0f766e" radius={[6, 6, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* TAB 2: FINANCIAL STATEMENTS (P&L) */}
                {activeTab === 'p_and_l' && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm overflow-hidden">
                            <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-slate-50/50">
                                <div>
                                    <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                        <Scale className="w-5 h-5 text-teal-600" />
                                        Formal Profit & Loss Statement (P&L)
                                    </h2>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        GAAP-aligned financial report for business owners, accountants, and tax authorities
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handleQuickExport('p_and_l', 'pdf')}
                                        className="flex items-center gap-1.5 px-3.5 py-2 bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 rounded-xl text-xs font-bold shadow-xs transition-all"
                                    >
                                        <FileText className="w-3.5 h-3.5" /> Export Company PDF
                                    </button>
                                    <button
                                        onClick={() => handleQuickExport('p_and_l', 'excel')}
                                        className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-xs transition-all"
                                    >
                                        <FileSpreadsheet className="w-3.5 h-3.5" /> Export Excel Spreadsheet
                                    </button>
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="bg-slate-100/70 border-b border-slate-200 text-slate-600 font-bold uppercase text-[10px] tracking-wider">
                                            <th className="py-3 px-6 text-left">Line Item</th>
                                            <th className="py-3 px-6 text-left">Category</th>
                                            <th className="py-3 px-6 text-right">Amount (NGN)</th>
                                            <th className="py-3 px-6 text-right">% of Net Sales</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 font-medium">
                                        {/* Operating Revenue */}
                                        <tr className="bg-slate-50/80 font-bold text-slate-900">
                                            <td colSpan={4} className="py-2.5 px-6 uppercase text-[10px] tracking-wider text-teal-800">
                                                1. Operating Revenue
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-6 pl-10 text-slate-800">Gross Sales Revenue</td>
                                            <td className="py-3 px-6 text-slate-500">Gross Sales Volume</td>
                                            <td className="py-3 px-6 text-right font-bold text-slate-900">{formatNaira(grossSales)}</td>
                                            <td className="py-3 px-6 text-right text-slate-400">—</td>
                                        </tr>
                                        <tr className="text-rose-600">
                                            <td className="py-2.5 px-6 pl-10">Less: Sales Discounts & Allowances</td>
                                            <td className="py-2.5 px-6 text-slate-400">Promotions & Markdowns</td>
                                            <td className="py-2.5 px-6 text-right font-semibold">- {formatNaira(summary.discounts)}</td>
                                            <td className="py-2.5 px-6 text-right text-slate-400">{netSales > 0 ? ((parseFloat(summary.discounts || 0) / netSales) * 100).toFixed(1) : 0}%</td>
                                        </tr>
                                        <tr className="text-rose-600">
                                            <td className="py-2.5 px-6 pl-10">Less: Returns & Order Refunds</td>
                                            <td className="py-2.5 px-6 text-slate-400">Customer Returns</td>
                                            <td className="py-2.5 px-6 text-right font-semibold">- {formatNaira(summary.refunds)}</td>
                                            <td className="py-2.5 px-6 text-right text-slate-400">{netSales > 0 ? ((parseFloat(summary.refunds || 0) / netSales) * 100).toFixed(1) : 0}%</td>
                                        </tr>
                                        <tr className="bg-emerald-50/50 font-bold text-emerald-950">
                                            <td className="py-3 px-6 pl-8">NET OPERATING SALES REVENUE</td>
                                            <td className="py-3 px-6 text-emerald-700">Net Sales</td>
                                            <td className="py-3 px-6 text-right font-bold text-sm">{formatNaira(netSales)}</td>
                                            <td className="py-3 px-6 text-right font-bold">100.0%</td>
                                        </tr>

                                        {/* Cost of Goods Sold */}
                                        <tr className="bg-slate-50/80 font-bold text-slate-900">
                                            <td colSpan={4} className="py-2.5 px-6 uppercase text-[10px] tracking-wider text-teal-800">
                                                2. Cost of Goods Sold (COGS)
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="py-3 px-6 pl-10 text-slate-800">Direct Inventory & Recipe Ingredient Costs</td>
                                            <td className="py-3 px-6 text-slate-500">COGS</td>
                                            <td className="py-3 px-6 text-right font-bold text-rose-600">- {formatNaira(cogs)}</td>
                                            <td className="py-3 px-6 text-right text-slate-600">{netSales > 0 ? ((cogs / netSales) * 100).toFixed(1) : 0}%</td>
                                        </tr>
                                        <tr className="bg-amber-50/50 font-bold text-amber-950">
                                            <td className="py-3 px-6 pl-8">GROSS OPERATING PROFIT</td>
                                            <td className="py-3 px-6 text-amber-700">Gross Margin</td>
                                            <td className="py-3 px-6 text-right font-bold text-sm">{formatNaira(grossProfit)}</td>
                                            <td className="py-3 px-6 text-right font-bold">{summary.grossMarginPct || 0}%</td>
                                        </tr>

                                        {/* Operating Overhead */}
                                        <tr className="bg-slate-50/80 font-bold text-slate-900">
                                            <td colSpan={4} className="py-2.5 px-6 uppercase text-[10px] tracking-wider text-teal-800">
                                                3. Operating Expenses & Overhead
                                            </td>
                                        </tr>
                                        {(pandlData?.expenseBreakdown || []).length === 0 ? (
                                            <tr>
                                                <td colSpan={4} className="py-3 px-6 pl-10 text-slate-400 italic">
                                                    No operating expenses recorded for this period
                                                </td>
                                            </tr>
                                        ) : (
                                            pandlData.expenseBreakdown.map((exp: any) => (
                                                <tr key={exp.categoryName} className="hover:bg-slate-50/50">
                                                    <td className="py-2.5 px-6 pl-10 text-slate-700">{exp.categoryName}</td>
                                                    <td className="py-2.5 px-6 text-slate-400">{exp.expenseCount} vouchers</td>
                                                    <td className="py-2.5 px-6 text-right font-semibold text-rose-600">- {formatNaira(exp.amount)}</td>
                                                    <td className="py-2.5 px-6 text-right text-slate-500">{exp.expenseSharePct}%</td>
                                                </tr>
                                            ))
                                        )}
                                        <tr className="bg-rose-50/50 font-bold text-rose-950">
                                            <td className="py-3 px-6 pl-8">TOTAL OPERATING EXPENSES</td>
                                            <td className="py-3 px-6 text-rose-700">Overhead Overhead</td>
                                            <td className="py-3 px-6 text-right font-bold text-sm">- {formatNaira(totalExpenses)}</td>
                                            <td className="py-3 px-6 text-right font-bold">{netSales > 0 ? ((totalExpenses / netSales) * 100).toFixed(1) : 0}%</td>
                                        </tr>

                                        {/* Net Profit EBITDA */}
                                        <tr className="bg-gradient-to-r from-emerald-100/90 to-teal-100/90 font-bold text-slate-900 text-sm">
                                            <td className="py-4 px-6 uppercase tracking-wider text-emerald-950">
                                                NET OPERATING PROFIT (EBITDA)
                                            </td>
                                            <td className="py-4 px-6 text-emerald-800 text-xs font-bold">
                                                Bottom Line Net Margin
                                            </td>
                                            <td className="py-4 px-6 text-right text-base text-emerald-800">
                                                {formatNaira(netOperatingProfit)}
                                            </td>
                                            <td className="py-4 px-6 text-right text-emerald-800">
                                                {summary.netProfitMarginPct || 0}%
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* TAB 3: REPORT CATALOG (23 REPORTS) */}
                {activeTab === 'catalog' && (
                    <div className="space-y-6">
                        {/* Search and Category Filters */}
                        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-xs flex flex-col md:flex-row gap-3 justify-between items-center">
                            <div className="relative w-full md:w-96">
                                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search 23 ERP reports (e.g. Tax, Stock, P&L, Cashier)..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                                />
                            </div>

                            <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto">
                                {[
                                    { key: 'all', label: 'All Reports' },
                                    { key: 'financial', label: 'Financial' },
                                    { key: 'sales', label: 'Sales' },
                                    { key: 'inventory', label: 'Inventory' },
                                    { key: 'customer', label: 'Customers' },
                                    { key: 'staff', label: 'Staff' },
                                ].map(c => (
                                    <button
                                        key={c.key}
                                        onClick={() => setActiveCategory(c.key)}
                                        className={clsx(
                                            'px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap',
                                            activeCategory === c.key
                                                ? 'bg-teal-600 text-white shadow-xs'
                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                        )}
                                    >
                                        {c.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Sections & Cards */}
                        {filteredSections.map((section) => (
                            <div key={section.category} className="space-y-3">
                                <div className="flex items-center gap-2.5">
                                    <div className={`p-1.5 rounded-xl bg-gradient-to-br ${section.gradient} text-white shadow-xs`}>
                                        <section.icon className="w-3.5 h-3.5" />
                                    </div>
                                    <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                                        {section.category}
                                    </h3>
                                    <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                                        {section.reports.length}
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {section.reports.map((report) => (
                                        <div
                                            key={report.href}
                                            onClick={() => navigate(report.href)}
                                            className="group bg-white rounded-2xl border border-slate-200 p-4 hover:border-teal-400 hover:shadow-lg transition-all cursor-pointer flex flex-col justify-between"
                                        >
                                            <div>
                                                <div className="flex items-start justify-between gap-2 mb-3">
                                                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${report.color} flex items-center justify-center text-white shadow-xs`}>
                                                        <report.icon size={18} />
                                                    </div>
                                                    {report.badge && (
                                                        <Badge variant={report.badge === 'Alert' ? 'danger' : 'warning'} size="sm">
                                                            {report.badge}
                                                        </Badge>
                                                    )}
                                                </div>
                                                <h4 className="font-bold text-sm text-slate-900 group-hover:text-teal-700 transition-colors">
                                                    {report.title}
                                                </h4>
                                                <p className="text-xs text-slate-400 mt-1 line-clamp-2">
                                                    {report.description}
                                                </p>
                                            </div>

                                            {/* Action bar with 1-click tenant exports */}
                                            <div className="flex items-center justify-between pt-3 mt-3 border-t border-slate-100">
                                                <span className="text-[11px] font-bold text-teal-600 flex items-center gap-0.5 group-hover:translate-x-0.5 transition-transform">
                                                    Open <ChevronRight size={12} />
                                                </span>
                                                <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        onClick={() => handleQuickExport(report.type, 'pdf')}
                                                        className="px-2 py-1 bg-slate-100 hover:bg-rose-50 hover:text-rose-600 rounded-lg text-[10px] font-bold text-slate-600 transition-colors flex items-center gap-1"
                                                        title="Download PDF with Tenant Header"
                                                    >
                                                        <FileText size={11} /> PDF
                                                    </button>
                                                    <button
                                                        onClick={() => handleQuickExport(report.type, 'excel')}
                                                        className="px-2 py-1 bg-slate-100 hover:bg-emerald-50 hover:text-emerald-700 rounded-lg text-[10px] font-bold text-slate-600 transition-colors flex items-center gap-1"
                                                        title="Download XLSX with Tenant Header"
                                                    >
                                                        <FileSpreadsheet size={11} /> XLSX
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* TAB 4: UNIVERSAL EXPORT CENTER */}
                {activeTab === 'export_center' && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 pb-4 border-b border-slate-100">
                                <div>
                                    <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                        <Download className="w-5 h-5 text-teal-600" />
                                        Universal Tenant Export Center
                                    </h2>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        Every PDF and XLSX file includes your company name, logo, address, RC/Tax ID, and branch metadata
                                    </p>
                                </div>
                                <div className="text-xs text-slate-500 bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                                    Active Filter: <span className="font-bold text-slate-800">{effectiveDates.startDate || 'All Time'} to {effectiveDates.endDate || 'Present'}</span>
                                    {selectedBranchId && <span> · Branch Selected</span>}
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="bg-slate-100/70 border-b border-slate-200 text-slate-600 font-bold uppercase text-[10px] tracking-wider">
                                            <th className="py-3 px-5 text-left">Report Title</th>
                                            <th className="py-3 px-5 text-left">Category</th>
                                            <th className="py-3 px-5 text-left">Description</th>
                                            <th className="py-3 px-5 text-right">Instant Tenant Export</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 font-medium">
                                        {REPORT_SECTIONS.flatMap(s => s.reports.map(r => ({ ...r, categoryName: s.category }))).map((rep) => (
                                            <tr key={rep.type} className="hover:bg-slate-50/60 transition-colors">
                                                <td className="py-3 px-5 font-bold text-slate-900 flex items-center gap-2">
                                                    <rep.icon size={15} className="text-teal-600 shrink-0" />
                                                    <span>{rep.title}</span>
                                                </td>
                                                <td className="py-3 px-5 text-slate-500">{rep.categoryName}</td>
                                                <td className="py-3 px-5 text-slate-400 truncate max-w-xs">{rep.description}</td>
                                                <td className="py-3 px-5 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={() => handleQuickExport(rep.type, 'pdf')}
                                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-xl text-xs font-bold transition-all shadow-xs"
                                                        >
                                                            <FileText size={12} /> PDF
                                                        </button>
                                                        <button
                                                            onClick={() => handleQuickExport(rep.type, 'excel')}
                                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-xl text-xs font-bold transition-all shadow-xs"
                                                        >
                                                            <FileSpreadsheet size={12} /> Excel (XLSX)
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// Subcomponents

function TabButton({ active, onClick, icon: Icon, label, badge }: {
    active: boolean; onClick: () => void; icon: React.ElementType; label: string; badge?: string;
}) {
    return (
        <button
            onClick={onClick}
            className={clsx(
                'flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap',
                active
                    ? 'bg-white text-teal-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-900'
            )}
        >
            <Icon size={16} />
            <span>{label}</span>
            {badge && (
                <span className={clsx(
                    'px-2 py-0.5 rounded-full text-[10px] font-bold',
                    active ? 'bg-teal-50 text-teal-700 border border-teal-200' : 'bg-slate-200 text-slate-600'
                )}>
                    {badge}
                </span>
            )}
        </button>
    );
}

function StatWidget({ label, value, subtitle, icon: Icon, gradient }: {
    label: string; value: string; subtitle: string; icon: React.ElementType; gradient: string;
}) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -2 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 hover:shadow-lg transition-all flex flex-col justify-between"
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider truncate">{label}</p>
                    <p className="text-xl font-bold text-slate-900 mt-1 truncate">{value}</p>
                </div>
                <div className={`p-2.5 rounded-xl bg-gradient-to-br ${gradient} shadow-md shrink-0 text-white`}>
                    <Icon className="w-4 h-4" />
                </div>
            </div>
            <p className="text-[11px] text-slate-400 mt-2 truncate">{subtitle}</p>
        </motion.div>
    );
}

function InsightCard({ title, description, icon: Icon, color }: {
    title: string; description: string; icon: React.ElementType; color: 'emerald' | 'blue' | 'amber' | 'purple';
}) {
    const colorStyles = {
        emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
        blue: 'bg-blue-50 text-blue-800 border-blue-200',
        amber: 'bg-amber-50 text-amber-800 border-amber-200',
        purple: 'bg-purple-50 text-purple-800 border-purple-200',
    };
    return (
        <div className={`p-4 rounded-2xl border ${colorStyles[color]} flex flex-col justify-between shadow-sm`}>
            <div>
                <div className="flex items-center gap-2 mb-1.5">
                    <Icon size={16} />
                    <h4 className="font-bold text-xs uppercase tracking-wider">{title}</h4>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">{description}</p>
            </div>
        </div>
    );
}